import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { AccessTime, Refresh, Logout } from '@mui/icons-material';
import { useShallow } from 'zustand/react/shallow';
import { useAuthStore } from '../../stores';

// Show warning 10 minutes before expiration
const WARNING_THRESHOLD_MS = 10 * 60 * 1000;
// Check every 30 seconds
const CHECK_INTERVAL_MS = 30 * 1000;
// Remind me later delay (1 minute)
const REMIND_LATER_DELAY_MS = 60 * 1000;
// Auto-extend threshold - try to auto-extend when 15 minutes remain (before showing warning)
const AUTO_EXTEND_THRESHOLD_MS = 15 * 60 * 1000;

interface SessionExpirationWarningProps {
  onExtendSession?: () => void;
}

const SessionExpirationWarning: React.FC<SessionExpirationWarningProps> = ({ onExtendSession }) => {
  const { logout, isAuthenticated } = useAuthStore(
    useShallow((s) => ({ logout: s.logout, isAuthenticated: s.isAuthenticated }))
  );
  const [showWarning, setShowWarning] = useState(false);
  const [showExpiredSnackbar, setShowExpiredSnackbar] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isExtending, setIsExtending] = useState(false);
  const [suppressUntil, setSuppressUntil] = useState<number | null>(null);
  const [hasRefreshToken, setHasRefreshToken] = useState(false);
  const [autoExtendAttempted, setAutoExtendAttempted] = useState(false);
  const [showAutoExtendSuccess, setShowAutoExtendSuccess] = useState(false);

  const getTimeRemaining = useCallback(() => {
    const expiresAt = localStorage.getItem('token_expires_at');
    if (!expiresAt) return null;
    return parseInt(expiresAt, 10) - Date.now();
  }, []);

  const formatTimeRemaining = (ms: number): string => {
    if (ms <= 0) return 'Expired';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ${seconds} second${seconds !== 1 ? 's' : ''}`;
    }
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  };

  // Auto-extend session silently when we have a refresh token
  const autoExtendSession = useCallback(async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;
    
    try {
      console.log('[Session] Auto-extending session silently...');
      const apiService = (await import('../../services/api')).default;
      const authResponse = await apiService.refreshToken(refreshToken);
      
      // Update tokens in localStorage
      localStorage.setItem('access_token', authResponse.access_token);
      
      if (authResponse.refresh_token) {
        localStorage.setItem('refresh_token', authResponse.refresh_token);
      }
      
      if (authResponse.expires_in) {
        const expiresAt = Date.now() + (authResponse.expires_in * 1000);
        localStorage.setItem('token_expires_at', expiresAt.toString());
      }
      
      console.log('[Session] Session auto-extended successfully!');
      setShowAutoExtendSuccess(true);
      onExtendSession?.();
      return true;
    } catch (error) {
      console.warn('[Session] Auto-extend failed:', error);
      return false;
    }
  }, [onExtendSession]);

  const checkExpiration = useCallback(async () => {
    if (!isAuthenticated) return;

    const remaining = getTimeRemaining();
    
    if (remaining === null) return;

    setTimeRemaining(remaining);

    if (remaining <= 0) {
      // Token has expired
      setShowWarning(false);
      setShowExpiredSnackbar(true);
      // Auto-logout after a brief delay
      setTimeout(() => {
        logout();
      }, 3000);
    } else if (remaining <= AUTO_EXTEND_THRESHOLD_MS && remaining > WARNING_THRESHOLD_MS && !autoExtendAttempted) {
      // Try to auto-extend session silently before showing warning
      setAutoExtendAttempted(true);
      const success = await autoExtendSession();
      if (success) {
        // Session extended, reset the auto-extend flag for next cycle
        setTimeout(() => setAutoExtendAttempted(false), AUTO_EXTEND_THRESHOLD_MS);
      }
      // If auto-extend fails, the next check will show the warning dialog
    } else if (remaining <= WARNING_THRESHOLD_MS && !showWarning) {
      // Check if we should suppress the warning (user clicked "Remind Me Later")
      if (suppressUntil && Date.now() < suppressUntil) {
        return; // Don't show yet
      }
      // Show warning when within threshold
      setHasRefreshToken(Boolean(localStorage.getItem('refresh_token')));
      setShowWarning(true);
      setSuppressUntil(null); // Clear suppression once shown
    }
  }, [isAuthenticated, getTimeRemaining, showWarning, logout, suppressUntil, autoExtendAttempted, autoExtendSession]);

  useEffect(() => {
    // Initial check
    checkExpiration();

    // Set up interval for periodic checks
    const interval = setInterval(checkExpiration, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [checkExpiration]);

  // Update countdown every second when warning is shown
  useEffect(() => {
    if (!showWarning) return;

    const countdown = setInterval(() => {
      const remaining = getTimeRemaining();
      if (remaining !== null) {
        setTimeRemaining(remaining);
        if (remaining <= 0) {
          setShowWarning(false);
          setShowExpiredSnackbar(true);
          setTimeout(() => logout(), 3000);
        }
      }
    }, 1000);

    return () => clearInterval(countdown);
  }, [showWarning, getTimeRemaining, logout]);

  const handleExtendSession = async () => {
    setIsExtending(true);
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (refreshToken) {
        // Try to refresh using refresh token (silent refresh - no redirect!)
        const apiService = (await import('../../services/api')).default;
        const authResponse = await apiService.refreshToken(refreshToken);
        
        // Update tokens in localStorage
        localStorage.setItem('access_token', authResponse.access_token);
        
        // Update refresh token if new one provided
        if (authResponse.refresh_token) {
          localStorage.setItem('refresh_token', authResponse.refresh_token);
        }
        
        // Update expiration time
        if (authResponse.expires_in) {
          const expiresAt = Date.now() + (authResponse.expires_in * 1000);
          localStorage.setItem('token_expires_at', expiresAt.toString());
        }
        
        // Success! Close dialog without any redirect
        setShowWarning(false);
        setIsExtending(false);
        setShowSuccessSnackbar(true);
        onExtendSession?.();
        
        console.log('[Session] Token refreshed silently - no redirect needed');
        return; // Important: exit here to avoid any redirect
      }
      
      // No refresh token - this UI should not show Extend Session in that case, but keep a safe fallback.
      console.warn('[Session] No refresh token available (cannot extend silently)');
      setIsExtending(false);
      setErrorMessage('This session cannot be extended automatically. Please save your work and log in again.');
      setShowErrorSnackbar(true);
      
    } catch (error) {
      console.error('Failed to extend session:', error);
      setIsExtending(false);
      setErrorMessage('Session extension failed. Please save your work and log in again.');
      setShowErrorSnackbar(true);
    }
  };

  const handleLogout = () => {
    setShowWarning(false);
    logout();
  };

  const handleRemindLater = () => {
    // Suppress warning for 1 minute, then remind again
    setShowWarning(false);
    setSuppressUntil(Date.now() + REMIND_LATER_DELAY_MS);
  };

  const progressValue = timeRemaining !== null 
    ? Math.max(0, (timeRemaining / WARNING_THRESHOLD_MS) * 100)
    : 0;

  return (
    <>
      {/* Session Expiration Warning Dialog */}
      <Dialog
        open={showWarning}
        onClose={handleRemindLater}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            border: '1px solid rgba(255, 183, 77, 0.3)',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ffb74d' }}>
          <AccessTime sx={{ color: '#ffb74d' }} />
          Session Expiring Soon
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={progressValue} 
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(255, 183, 77, 0.2)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: progressValue > 50 ? '#ffb74d' : progressValue > 25 ? '#ff9800' : '#f44336',
                }
              }}
            />
          </Box>
          <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Your session will expire in{' '}
            <Typography component="span" sx={{ fontWeight: 'bold', color: '#ffb74d' }}>
              {timeRemaining !== null ? formatTimeRemaining(timeRemaining) : 'a few minutes'}
            </Typography>
            .
          </DialogContentText>
          {hasRefreshToken ? (
            <DialogContentText sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
              You can extend your session without leaving this page.
            </DialogContentText>
          ) : (
            <DialogContentText sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
              Automatic session extension isn’t available. Please save any unsaved work now. When your session expires, you’ll need to log in again.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleLogout}
            startIcon={<Logout />}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            {hasRefreshToken ? 'Logout Now' : 'Save & Log out'}
          </Button>
          <Button
            onClick={handleRemindLater}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Remind Me Later
          </Button>
          {hasRefreshToken && (
            <Button
              onClick={handleExtendSession}
              variant="contained"
              startIcon={<Refresh />}
              disabled={isExtending}
              sx={{
                background: 'linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #f57c00 30%, #ff9800 90%)',
                }
              }}
            >
              {isExtending ? 'Extending...' : 'Extend Session'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Session Expired Snackbar */}
      <Snackbar
        open={showExpiredSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowExpiredSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="warning" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          Your session has expired. Please log in again.
        </Alert>
      </Snackbar>

      {/* Session Extended Success Snackbar (manual) */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          Session extended successfully!
        </Alert>
      </Snackbar>

      {/* Session Auto-Extended Success Snackbar */}
      <Snackbar
        open={showAutoExtendSuccess}
        autoHideDuration={2000}
        onClose={() => setShowAutoExtendSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity="info" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          Session automatically extended
        </Alert>
      </Snackbar>

      {/* Session Extension Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="error" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SessionExpirationWarning;

